import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AuthenticationService } from '../../services/authentication.service';
import { ProfileCompletionService, ProfileField } from '../../services/profile-completion.service';

/**
 * "Complete your profile" banner.
 *
 * Shown on the checkout page only (shop/checkout and shop/checkout/:id), where
 * the billing details come from the profile. It used to sit on both account
 * dashboards; elsewhere the header badge is the only reminder. Works for
 * INDIAPICTURE and EXCLUSIVE accounts alike — only the destination differs,
 * which the service works out from the account.
 *
 * It follows currentUser rather than reading it once, so saving the profile
 * takes the banner away without a reload.
 */
@Component({
    selector: 'profile-alert',
    templateUrl: './profile-alert.component.html',
    styleUrls: ['./profile-alert.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileAlertComponent implements OnInit, OnDestroy {

    missing: ProfileField[] = [];
    link = '/account/profile';

    /** Hidden for this visit only. Nothing is stored: it is not a decision. */
    dismissed = false;

    private destroy$ = new Subject<void>();

    constructor(private authService: AuthenticationService,
                private profileCompletion: ProfileCompletionService,
                private cdref: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.authService.currentUser
            .pipe(takeUntil(this.destroy$))
            .subscribe(user => {
                this.missing = this.profileCompletion.missingFields(
                    this.profileCompletion.isIncomplete(user) ? user : null
                );
                this.link = this.profileCompletion.profileLink(user);
                this.cdref.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get show(): boolean {
        return !this.dismissed && this.missing.length > 0;
    }

    dismiss(): void {
        this.dismissed = true;
    }
}
